import React, { useEffect, useState } from 'react';
import Layout from './components/Layout';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import TableContainer from './components/TableContainer';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import AcaoGovernamentalModal from './components/modals/AcaoGovernamentalModal';
import PaginationBar from './components/PaginationBar';
import usePagination10 from './components/hooks/usePagination10';
import DataTable from './components/DataTable';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';

function AcaoGovernamentals() {
  const formatarCodigo = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 17);
    const partes = [
      digits.slice(0, 2),
      digits.slice(2, 5),
      digits.slice(5, 9),
      digits.slice(9, 10),
      digits.slice(10, 13),
      digits.slice(13, 17),
    ];
    return partes.filter(p => p).join('.');
  };
  const [list, setList] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'codigo', direction: 'asc' });
  const token = localStorage.getItem('token');
  const vinculo = JSON.parse(localStorage.getItem('vinculo') || '{}');

  const normalizeString = str => {
    if (!str) return '';
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
  };

  const sortedList = [...list].sort((a, b) => {
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return String(a[sortConfig.key]).localeCompare(String(b[sortConfig.key]), 'pt-BR') * dir;
  });

  const filteredList = sortedList.filter(row => {
    const termo = normalizeString(filtro);
    return (
      normalizeString(row.codigo).includes(termo) ||
      normalizeString(row.descricao).includes(termo)
    );
  });

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const { setPagination } = usePagination10(filteredList.length);

  const paginatedList = filteredList.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  const load = () => {
    setLoading(true);
    axios.get(`${API_BASE_URL.ACAO_GOVERNAMENTAL}/entidade/${vinculo.idEntidade}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => setList(Array.isArray(res.data) ? res.data : []))
      .catch(err => showDefaultModal('error', err?.response?.data?.error || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="bi bi-arrow-down-up ms-1" style={{ opacity: 0.3 }} />;
    return sortConfig.direction === 'asc'
      ? <i className="bi bi-sort-up ms-1" />
      : <i className="bi bi-sort-down ms-1" />;
  };

  useEffect(() => {
    setPagination.onFirstItems();
  }, [filtro]);

  const handleNew = () => { setSelected(null); setShowModal(true); };
  const handleEdit = () => {
    if (!selectedRow) return;
    setSelected(selectedRow);
    setShowModal(true);
  };
  const handleDelete = () => {
    if (!selectedRow) return;
    showConfirmModal('delete', async () => {
      try {
        await axios.delete(
          `${API_BASE_URL.ACAO_GOVERNAMENTAL}/${selectedRow.idEntidade}/${selectedRow.codigo}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showDefaultModal('success', 'Registro excluído');
        setSelectedRow(null);
        load();
      } catch (err) {
        showDefaultModal('error', err?.response?.data?.error || err?.response?.data || 'Erro ao excluir');
      }
    });
  };

  const handleSaved = () => { setShowModal(false); load(); };
  const handleRowClick = (row) => {
    setSelectedRow(prev => prev?.codigo === row.codigo ? null : row);
  };

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Ações Governamentais" icon="bi-diagram-3" />
        <PageContent>
          <div className="d-flex flex-column flex-lg-row justify-content-lg-between gap-2 mb-2 align-items-start align-items-lg-center">
            <div className="botao-grid-2col">
              <button className="btn btn-success btn-sm botao-igual" onClick={handleNew}>
                <i className="bi bi-plus-circle me-1" /> Novo
              </button>
              <button className="btn btn-primary btn-sm botao-igual" onClick={handleEdit} disabled={!selectedRow}>
                <i className="bi bi-pencil me-1" /> Editar
              </button>
              <button className="btn btn-danger btn-sm botao-igual" onClick={handleDelete} disabled={!selectedRow}>
                <i className="bi bi-trash me-1" /> Excluir
              </button>
            </div>
            <div className="input-group input-group-sm mt-2 mt-lg-0" style={{ maxWidth: '300px' }}>
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Buscar por código ou descrição"
                value={filtro}
                onChange={e => setFiltro(e.target.value.toUpperCase())}
              />
            </div>
          </div>

          <TableContainer isLoading={isLoading}>
            <DataTable style={{ fontSize: '0.8rem' }} head={
              <tr>
                <th style={{ width: '220px', cursor: 'pointer' }} onClick={() => handleSort('codigo')}>
                  Código {sortIcon('codigo')}
                </th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('descricao')}>
                  Descrição {sortIcon('descricao')}
                </th>
              </tr>}>
              <tbody>
                {paginatedList && paginatedList.length > 0 ? paginatedList.map((row) => (
                  <tr
                    key={row.codigo}
                    onClick={() => handleRowClick(row)}
                    className={selectedRow?.codigo === row.codigo ? 'table-primary' : ''}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="font-monospace">{formatarCodigo(row.codigo)}</td>
                    <td>{row.descricao}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={2} className="text-center">Nenhum registro encontrado.</td></tr>
                )}
              </tbody>
            </DataTable>
          </TableContainer>
        </PageContent>
        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>

      <AcaoGovernamentalModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSaved}
        data={selected}
      />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </Layout>
  );
}

export default AcaoGovernamentals;
